import React, { useEffect, useState } from "react";

const EXPLORE_API = "https://public-explorer.genexports.com";

// Your existing report Cloud Function URL
const REPORT_API = "https://sellerreport.genexports.com";

// Temporary hardcoded sale number
const SALE = 32;

export default function Explore() {
  const [sessionId, setSessionId] = useState(null);

  const [locations, setLocations] = useState([]);
  const [entities, setEntities] = useState([]);

  const [type, setType] = useState("");
  const [location, setLocation] = useState("");
  const [entity, setEntity] = useState("");

  const [report, setReport] = useState("");

  const [loading, setLoading] = useState(false);
  const [loadingEntities, setLoadingEntities] = useState(false);

  const [error, setError] = useState("");
  const [showAppGate, setShowAppGate] = useState(false);

  const [remaining, setRemaining] = useState(2);

  useEffect(() => {
    initialize();
  }, []);

  async function initialize() {
    try {
      setLoading(true);

      // Reuse session during this browser session.
      let savedSession = sessionStorage.getItem(
        "teaExploreSession"
      );

      if (!savedSession) {
        const response = await fetch(
          `${EXPLORE_API}/start`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Unable to start session");
        }

        const data = await response.json();

        savedSession = data.sessionId;

        sessionStorage.setItem(
          "teaExploreSession",
          savedSession
        );
      }

      setSessionId(savedSession);

      // Load locations
      const response = await fetch(
        `${EXPLORE_API}/locations`
      );

      if (!response.ok) {
        throw new Error("Unable to load locations");
      }

      const data = await response.json();

      setLocations(data.locations || []);
    } catch (e) {
      console.error(e);
      setError(
        "Unable to load exploration. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }

  function selectType(value) {
    setType(value);

    setLocation("");
    setEntity("");
    setEntities([]);

    setReport("");
    setError("");
    setShowAppGate(false);
  }

  async function selectLocation(value) {
    setLocation(value);
    setEntity("");
    setEntities([]);

    setReport("");
    setError("");

    if (!value || !type) {
      return;
    }

    try {
      setLoadingEntities(true);

      const endpoint =
        type === "SELLER"
          ? `${EXPLORE_API}/sellers?location=${encodeURIComponent(
              value
            )}`
          : `${EXPLORE_API}/buyers?location=${encodeURIComponent(
              value
            )}`;

      const response = await fetch(endpoint);

      if (!response.ok) {
        throw new Error(
          "Unable to load sellers/buyers"
        );
      }

      const data = await response.json();

      setEntities(data.list || []);
    } catch (e) {
      console.error(e);

      setError(
        "Unable to load the list. Please try again."
      );
    } finally {
      setLoadingEntities(false);
    }
  }

  async function viewAnalysis() {
    if (!sessionId || !type || !location || !entity) {
      return;
    }

    try {
      setLoading(true);
      setError("");
      setShowAppGate(false);

      // -----------------------------------------
      // Check free exploration
      // -----------------------------------------

      const checkResponse = await fetch(
        `${EXPLORE_API}/check-exploration`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            sessionId,
            type,
            entity,
            location,
          }),
        }
      );

      const check = await checkResponse.json();

      // User has used their free explorations
      if (
        checkResponse.status === 403 ||
        check.requiresApp === true
      ) {
        setShowAppGate(true);
        return;
      }

      if (!checkResponse.ok) {
        throw new Error(
          check.error || "Unable to check exploration"
        );
      }

      if (check.remaining !== undefined) {
        setRemaining(check.remaining);
      }

      // -----------------------------------------
      // Generate report
      // -----------------------------------------

      let reportResponse;

      if (type === "SELLER") {
        reportResponse = await fetch(
          `${REPORT_API}/seller-report-public`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              sessionId,
              mark: entity,
              sale: SALE,
              location,
              format: "html",
            }),
          }
        );
      } else {
        reportResponse = await fetch(
          `${REPORT_API}/buyer-report-public`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              sessionId,
              buyer: entity,
              sale: SALE,
              location,
              format: "html",
            }),
          }
        );
      }

      if (!reportResponse.ok) {

        if (reportResponse.status === 403) {
          setShowAppGate(true);
          return;
        }

        throw new Error(
          "Unable to generate report"
        );
      }

      const html = await reportResponse.text();

      setReport(html);

    } catch (e) {
      console.error(e);

      setError(
        "Unable to generate the analysis. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }

  function exploreAnother() {
    setType("");
    setLocation("");
    setEntity("");
    setEntities([]);
    setReport("");
    setError("");
    setShowAppGate(false);
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f7f7f7",
        padding: "40px 20px",
      }}
    >
      <div
        style={{
          maxWidth: 900,
          margin: "0 auto",
        }}
      >

        {/* Header */}

        {!report && !showAppGate && (
          <div
            style={{
              textAlign: "center",
              marginBottom: 35,
            }}
          >
            <h1>
              Explore Tea Auction Analysis
            </h1>

            <p
              style={{
                color: "#666",
                fontSize: 17,
              }}
            >
              Explore recent auction performance
              of sellers and buyers.
            </p>
          </div>
        )}

        {/* Selection */}

        {!report && !showAppGate && (
          <div
            style={{
              background: "#fff",
              padding: 30,
              borderRadius: 16,
              boxShadow:
                "0 4px 20px rgba(0,0,0,0.06)",
            }}
          >

            {/* Seller / Buyer */}

            <div style={{ marginBottom: 25 }}>
              <label
                style={{
                  display: "block",
                  fontWeight: 600,
                  marginBottom: 10,
                }}
              >
                What would you like to explore?
              </label>

              <div
                style={{
                  display: "flex",
                  gap: 12,
                }}
              >
                <button
                  onClick={() =>
                    selectType("SELLER")
                  }
                  style={{
                    flex: 1,
                    padding: 18,
                    borderRadius: 10,
                    border:
                      type === "SELLER"
                        ? "2px solid #111"
                        : "1px solid #ddd",
                    background:
                      type === "SELLER"
                        ? "#f5f5f5"
                        : "#fff",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  🏭 Seller
                </button>

                <button
                  onClick={() =>
                    selectType("BUYER")
                  }
                  style={{
                    flex: 1,
                    padding: 18,
                    borderRadius: 10,
                    border:
                      type === "BUYER"
                        ? "2px solid #111"
                        : "1px solid #ddd",
                    background:
                      type === "BUYER"
                        ? "#f5f5f5"
                        : "#fff",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  🛒 Buyer
                </button>
              </div>
            </div>

            {/* Location */}

            {type && (
              <div style={{ marginBottom: 25 }}>
                <label
                  style={{
                    display: "block",
                    fontWeight: 600,
                    marginBottom: 10,
                  }}
                >
                  Location
                </label>

                <select
                  value={location}
                  onChange={(e) =>
                    selectLocation(
                      e.target.value
                    )
                  }
                  style={{
                    width: "100%",
                    padding: 13,
                    borderRadius: 8,
                    border: "1px solid #ddd",
                    fontSize: 15,
                  }}
                >
                  <option value="">
                    Select location
                  </option>

                  {locations.map((item) => (
                    <option
                      key={item}
                      value={item}
                    >
                      {item}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Seller / Buyer */}

            {location && (
              <div style={{ marginBottom: 25 }}>
                <label
                  style={{
                    display: "block",
                    fontWeight: 600,
                    marginBottom: 10,
                  }}
                >
                  {type === "SELLER"
                    ? "Seller / Mark"
                    : "Buyer"}
                </label>

                {loadingEntities ? (
                  <p>Loading...</p>
                ) : (
                  <select
                    value={entity}
                    onChange={(e) =>
                      setEntity(e.target.value)
                    }
                    style={{
                      width: "100%",
                      padding: 13,
                      borderRadius: 8,
                      border: "1px solid #ddd",
                      fontSize: 15,
                    }}
                  >
                    <option value="">
                      {type === "SELLER"
                        ? "Select seller / mark"
                        : "Select buyer"}
                    </option>

                    {entities.map((item) => (
                      <option
                        key={item}
                        value={item}
                      >
                        {item}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* View */}

            {entity && (
              <button
                onClick={viewAnalysis}
                disabled={loading}
                style={{
                  width: "100%",
                  padding: 15,
                  border: "none",
                  borderRadius: 9,
                  background: "#111",
                  color: "#fff",
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {loading
                  ? "Preparing analysis..."
                  : "View Analysis"}
              </button>
            )}

            {/* Remaining */}

            <div
              style={{
                marginTop: 18,
                paddingTop: 15,
                borderTop: "1px solid #eee",
                display: "flex",
                justifyContent:
                  "space-between",
                color: "#777",
                fontSize: 13,
              }}
            >
              <span>
                Free explorations
              </span>

              <strong>
                {remaining} remaining
              </strong>
            </div>

            {error && (
              <div
                style={{
                  marginTop: 15,
                  padding: 12,
                  borderRadius: 8,
                  background: "#fff0f0",
                  color: "#c00",
                }}
              >
                {error}
              </div>
            )}

          </div>
        )}

        {/* Report */}

        {report && (
          <div>

            <div
              style={{
                display: "flex",
                justifyContent:
                  "space-between",
                alignItems: "center",
                marginBottom: 15,
              }}
            >
              <button
                onClick={exploreAnother}
                style={{
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                ← Explore another
              </button>

              <span
                style={{
                  color: "#666",
                  fontSize: 13,
                }}
              >
                {remaining} free{" "}
                {remaining === 1
                  ? "exploration"
                  : "explorations"}{" "}
                remaining
              </span>
            </div>

            <div
              style={{
                background: "#fff",
                borderRadius: 14,
                padding: 30,
                overflowX: "auto",
              }}
              dangerouslySetInnerHTML={{
                __html: report,
              }}
            />

          </div>
        )}

        {/* App Gate */}

        {showAppGate && (
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              padding: "50px 30px",
              textAlign: "center",
            }}
          >

            <div
              style={{
                fontSize: 50,
                marginBottom: 15,
              }}
            >
              📊
            </div>

            <h2>
              Get more with Tea Auction Analysis
            </h2>

            <p
              style={{
                maxWidth: 500,
                margin: "15px auto 30px",
                color: "#666",
                lineHeight: 1.6,
              }}
            >
              You've used your free explorations.
              Download the app to get detailed
              auction analysis, historical trends
              and more.
            </p>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 12,
              }}
            >
              <a
                href="#"
                style={{
                  padding: "13px 20px",
                  background: "#111",
                  color: "#fff",
                  borderRadius: 8,
                  textDecoration: "none",
                  fontWeight: 600,
                }}
              >
                iPhone
              </a>

              <a
                href="#"
                style={{
                  padding: "13px 20px",
                  background: "#111",
                  color: "#fff",
                  borderRadius: 8,
                  textDecoration: "none",
                  fontWeight: 600,
                }}
              >
                Android
              </a>
            </div>

            <div style={{ marginTop: 25 }}>
              <button
                onClick={exploreAnother}
                style={{
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                ← Back
              </button>
            </div>

          </div>
        )}

      </div>
    </div>
  );
}